import Chip from '@mui/material/Chip';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatchRoute } from '@tanstack/react-router';
import { pick } from 'lodash-es';
import { Suspense, useMemo, useState } from 'react';

import { PACKAGE_EDITOR_RATINGS_ROWS_FLAG } from '#components/feature-flag/flags.js';
import { useFeatureFlag } from '#components/feature-flag/use-feature-flag.js';
import { InlineSkeleton } from '#components/inline-skeleton/inline-skeleton.js';
import { Loading } from '#components/loading.js';
import { PackageValidationResultDisplay } from '#components/package-validation-result-display/package-validation-result-display.js';
import { Tabs } from '#components/tabs/tabs.js';
import { usePackageSidePanelStore } from '#package-store/package-family-editor/side-panel/package-side-panel-store.js';
import { useAddAgentWorkbenchPageContext } from '#screens/agent-workbench/agent-workbench-page-context.js';
import {
	AddAiExtractionContextPkgs,
	AddAiExtractionContextTemplates,
} from '#screens/ai-workbench/ai-extraction-context.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { AddToQueueSplitButton } from './add-to-queue-split-button.js';
import { ExceptionReviewBanner } from './exception-review-banner.js';
import { OpenDatasheetWindowButton } from './open-datasheet-window-button.js';
import { OrphanBoundariesBanner } from './orphan-boundaries-banner.js';
import { PackageStatusTag } from './package-status-tag.js';
import { PublishPackageButton } from './publish-package-button.js';
import { useOrphanBoundaryCounts } from './use-orphan-boundary-counts.js';

import type { HilPackage } from '@cadstrom/backend/shared';
import type { ComponentValidationResult } from '@cadstrom/sigma-engine-shared';
import type { TabDef } from '#components/tabs/tabs.js';
import type { IAiExtractionTemplate } from '#screens/ai-workbench/ai-extraction-context.js';

export const Route = createFileRoute('/_authorization-gate/package-store/$packageFamilyId/packages/$packageId')({
	staticData: {
		authorize: (ability) => ability.can('manage', 'package'),
	},
	beforeLoad: ({ params: { packageId }, context: { trpc } }) => {
		return {
			getPackageOptions: trpc.package.get.queryOptions({ id: packageId }, { refetchOnMount: false }),
		};
	},
	head: () => ({ meta: [{ title: 'Package editor • Cadstrom' }] }),
	component: PackageEditorLayout,
});

function PackageEditorLayout() {
	const { getPackageOptions } = Route.useRouteContext();
	const [validationResult, setValidationResult] = useState<ComponentValidationResult | undefined>(undefined);

	const { data: pkgData } = useQuery(getPackageOptions);
	const showsRowsView = useFeatureFlag(PACKAGE_EDITOR_RATINGS_ROWS_FLAG) === true;

	const pkg = pkgData?.pkg;

	const numParameters =
		pkg ?
			Object.values(pkg.sigmaModelParameters).filter(
				(v) => (v.value !== null && v.value !== '') || (v.variance !== null && v.variance !== ''),
			).length
		:	0;
	const numSignalDriveModes =
		pkg ? pkg.signals.reduce((count, signal) => count + signal.supportedDriveModes.length, 0) : 0;
	const tabDefs = useMemo((): TabDef[] => {
		return [
			{
				id: 'general',
				label: 'General',
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/general' },
			},
			{
				id: 'datasheet',
				label: 'Datasheet',
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/datasheet' },
			},
			{
				id: 'signals',
				label: 'Signals',
				labelAdornEnd: pkg?.signals && pkg.signals.length > 0 && (
					<Chip label={pkg.signals.length} variant='outlined' className='tabular-nums' />
				),
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/signals' },
			},
			{
				id: 'pinout',
				label: 'Pinout',
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/pinout' },
			},
			{
				id: 'parameters',
				label: 'Parameters',
				labelAdornEnd: numParameters > 0 && <Chip label={numParameters} variant='outlined' className='tabular-nums' />,
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/parameters' },
			},
			{
				id: 'modes',
				label: 'Modes',
				labelAdornEnd: pkg?.modes && pkg.modes.length > 0 && (
					<Chip label={pkg.modes.length} variant='outlined' className='tabular-nums' />
				),
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/modes' },
			},
			{
				id: 'boundaries',
				label: 'Boundaries',
				labelAdornEnd: pkg && pkg.boundaries.length > 0 && (
					<Chip label={pkg.boundaries.length} variant='outlined' className='tabular-nums' />
				),
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/boundaries' },
			},
			{
				id: 'functions',
				label: 'Functions',
				labelAdornEnd: pkg && pkg.functions.length > 0 && (
					<Chip label={pkg.functions.length} variant='outlined' className='tabular-nums' />
				),
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/functions' },
			},
			{
				id: 'implementation-rules',
				label: 'Implementation rules',
				labelAdornEnd: pkg?.implementationRules && (
					<Chip
						label={pkg.implementationRules.reduce((count, section) => count + section.rules.length, 0)}
						variant='outlined'
						className='tabular-nums'
					/>
				),
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/implementation-rules' },
			},
			{
				id: 'resources',
				label: 'Resources',
				labelAdornEnd: pkg && pkg.resources.length > 0 && (
					<Chip label={pkg.resources.length} variant='outlined' className='tabular-nums' />
				),
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/resources' },
			},
			{
				id: 'signal-drive-modes',
				label: 'Signal drive modes',
				labelAdornEnd: numSignalDriveModes > 0 && (
					<Chip label={numSignalDriveModes} variant='outlined' className='tabular-nums' />
				),
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/signal-drive-modes' },
			},
			{
				id: 'models',
				label: 'Models',
				labelAdornEnd: pkg && pkg.spiceModels.length > 0 && (
					<Chip label={pkg.spiceModels.length} variant='outlined' className='tabular-nums' />
				),
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/models' },
			},
			{
				id: 'raw',
				label: 'Raw',
				linkProps: { to: '/package-store/$packageFamilyId/packages/$packageId/raw' },
			},
		] satisfies TabDef[];
	}, [pkg, numParameters, numSignalDriveModes]);

	const templates: IAiExtractionTemplate[] = useMemo(
		() =>
			pkg ?
				[
					{ name: 'mpn', value: pkg.manufacturerPartNumber, description: 'The name of the package' },
					{
						name: 'signals',
						value: () => JSON.stringify(pkg.signals.map((signal) => pick(signal, ['id', 'name', 'description']))),
						description: 'The signals available on the package',
						displayValue: pkg.signals.map((signal) => signal.name).join(', '),
					},
				]
			:	[],
		[pkg],
	);

	const matchRoute = useMatchRoute();
	const currentTab = tabDefs.find(({ linkProps }) => matchRoute({ ...linkProps, fuzzy: true }) !== false);

	const { packageId, packageFamilyId } = Route.useParams();
	useAddAgentWorkbenchPageContext(
		'Package Store',
		{
			'Selected package ID': packageId,
			'Selected package (MPN)': pkg?.manufacturerPartNumber,
			'Selected package family ID': packageFamilyId,
			'Datasheet document file ID': pkg?.fileId ?? undefined,
			'Active editor tab': currentTab?.label,
		},
		{ package_id: packageId, package_mpn: pkg?.manufacturerPartNumber },
	);

	const el = useDataElTagGenerator(PackageEditorLayout);
	return (
		<>
			<div {...el()} className='flex grow flex-col overflow-hidden'>
				<div {...el('header')} className='flex flex-col gap-3 border-b border-gray-a4 p-4 pb-0'>
					<div className='flex items-start justify-between'>
						<div className='flex flex-col gap-2'>
							<div className='flex items-center gap-3'>
								<InlineSkeleton
									data={[pkg]}
									render={(pkg) => <h2 className='text-xl font-semibold'>Package {pkg.manufacturerPartNumber}</h2>}
									slotProps={{
										skeleton: { width: 100 },
									}}
								/>
								{pkg && <PackageStatusTag packageId={pkg.id} />}
							</div>
						</div>

						{pkg && (
							<div className='flex items-center gap-2'>
								<AddToQueueSplitButton packageId={pkg.id} />

								<div className='mx-1 w-px shrink-0 grow-0 basis-px self-stretch bg-gray-a4' />

								<OpenDatasheetWindowButton packageId={pkg.id} fileId={pkg.fileId} mpn={pkg.manufacturerPartNumber} />

								<div className='mx-1 w-px shrink-0 grow-0 basis-px self-stretch bg-gray-a4' />

								<PublishPackageButton
									pkg={pkg}
									validationResult={validationResult}
									onValidationResultChange={setValidationResult}
								/>
							</div>
						)}
					</div>

					{pkg && <ExceptionReviewBanner packageId={pkg.id} />}
					{pkg && showsRowsView && <PackageOrphanBoundariesBanner pkg={pkg} />}

					{validationResult && (
						<div {...el('validationResults')} className='-mx-0.5'>
							<PackageValidationResultDisplay validationResult={validationResult} />
						</div>
					)}

					<Tabs tabs={tabDefs} selectedTab={currentTab?.id ?? null} />
				</div>

				<div {...el('content')} className='flex grow basis-0 flex-col overflow-hidden'>
					<Suspense fallback={<Loading />}>
						<Outlet key={pkg?.id} />
					</Suspense>
				</div>
			</div>

			{pkg && <AddAiExtractionContextPkgs value={[pkg]} />}
			<AddAiExtractionContextTemplates value={templates} />
		</>
	);
}

/**
 * The orphaned-boundaries banner for one package: shown while the package has stored bounds none of
 * its rating rows stands for, counted live while its Boundaries tab is open.
 *
 * @param props - The package as saved.
 * @returns The banner, or nothing while every bound is covered.
 */
function PackageOrphanBoundariesBanner({ pkg }: { pkg: HilPackage }) {
	const saved = useOrphanBoundaryCounts(pkg);
	// Live from the Boundaries tab while it is mounted, so the count drops as rows are entered, before
	// the save; the saved package's counts otherwise.
	const live = usePackageSidePanelStore((state) => state.uncoveredBoundaryCounts);
	const { amrCount, ocCount } = live ?? saved;
	return (
		<OrphanBoundariesBanner
			packageFamilyId={pkg.pkgFamilyId}
			packageId={pkg.id}
			amrCount={amrCount}
			ocCount={ocCount}
		/>
	);
}
